import { api, unwrap, type ExportOut } from "../../api/client";
import { ExportButtons } from "../../components/ExportButtons";
import { ExportTable } from "../../components/ExportTable";
import { ErrorMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { exportDownloadUrl, usePollPendingExports } from "../../lib/exports";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { useKommune } from "./KommuneContext";

export function ExportsPage() {
  const { orgId, nr, navn, org, overview } = useKommune();
  usePageTitle(`${t("exports.title")} – ${navn}`);
  const list = useQuery(`exports:${orgId}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/exports", {
        params: { path: { org_id: orgId } },
        signal,
      }),
    ),
  );
  usePollPendingExports(list.data, list.reload);
  function queued(item: ExportOut) {
    list.setData((prev) => [item, ...(prev ?? []).filter((e) => e.id !== item.id)]);
    list.reload();
  }
  const kommuneName = (nummer: string) =>
    org.kommuner.find((k) => k.nummer === nummer)?.navn || nummer;
  const schoolName = (schoolId: string) =>
    overview?.schools.find((s) => s.school_id === schoolId)?.name ?? schoolId;
  return (
    <div className="page">
      <header className="page-header">
        <h1>{t("exports.title")}</h1>
        <p>{t("exports.intro")}</p>
      </header>
      <section className="card" aria-labelledby="exports-kommune-heading">
        <h2 id="exports-kommune-heading">{t("exports.kommuneTitle")}</h2>
        <ExportButtons orgId={orgId} nr={nr} disabled={!overview?.run} onQueued={queued} />
      </section>
      {!!overview?.schools.length && (
        <section className="section" aria-labelledby="exports-schools-heading">
          <h2 id="exports-schools-heading">{t("exports.schoolsTitle")}</h2>
          <div className="table-wrap">
            <table className="data-table">
              <caption className="visually-hidden">{t("exports.schoolsCaption")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("exports.colSchool")}</th>
                  <th scope="col">{t("exports.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {overview.schools.map((s) => (
                  <tr key={s.school_id}>
                    <th scope="row">{s.name}</th>
                    <td>
                      <ExportButtons
                        orgId={orgId}
                        nr={nr}
                        schoolId={s.school_id}
                        schoolName={s.name}
                        onQueued={queued}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section className="section" aria-labelledby="exports-list-heading">
        <h2 id="exports-list-heading">{t("exports.listTitle")}</h2>
        {list.error && <ErrorMessage error={list.error} onRetry={list.reload} />}
        {!list.data && !list.error && <Loading />}
        {list.data?.length === 0 && <p>{t("exports.listEmpty")}</p>}
        {!!list.data?.length && (
          <ExportTable
            exports={list.data}
            caption={t("exports.listCaption")}
            kommuneName={kommuneName}
            schoolName={schoolName}
            downloadHref={(e) => exportDownloadUrl.org(orgId, e.id)}
          />
        )}
      </section>
    </div>
  );
}
